import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Minus, Plus, TimeFilled } from '@teo/components/icons';
import { Button } from '@teo/components';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';

type Segment = 'h' | 'm' | 's';

export interface EstimatedTimeInputProps {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  allowSeconds?: boolean;
  maxHours?: number;
  label?: string;
  className?: string;
}

function clamp(n: number, min: number, max: number) {
  return Math.min(Math.max(n, min), max);
}

function toHMS(totalSeconds: number) {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  return { hours, minutes, seconds };
}

function fromHMS(h: number, m: number, s: number) {
  return h * 3600 + m * 60 + s;
}

function two(n: number) {
  return n.toString().padStart(2, '0');
}

export default function EstimatedTimeInput({
  value,
  onChange,
  disabled,
  allowSeconds = true,
  maxHours = 99,
  label,
  className,
}: EstimatedTimeInputProps) {
  const { hours, minutes, seconds } = useMemo(() => toHMS(value), [value]);
  const [open, setOpen] = useState(false);
  const [segment, setSegment] = useState<Segment>('h');
  const [buffer, setBuffer] = useState<string>('');

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();

  const [position, setPosition] = useState({
    top: 0,
    left: 0,
  });

  useEffect(() => {
    function onDocPointerDown(e: MouseEvent) {
      const t = e.target as Node;
      if (!wrapperRef.current?.contains(t) && !popupRef.current?.contains(t)) {
        setOpen(false);
        setBuffer('');
      }
    }
    document.addEventListener('pointerdown', onDocPointerDown);
    return () => document.removeEventListener('pointerdown', onDocPointerDown);
  }, []);

  useEffect(() => {
    if (!open) return;
    const el = inputRef.current;
    if (!el) return;
    const ranges = segmentRanges(
      displayValue(hours, minutes, seconds, allowSeconds),
      allowSeconds
    );
    const r = ranges[segment];

    requestAnimationFrame(() => {
      try {
        el.setSelectionRange(r.end, r.end);
      } catch {
        /* noop */
      }
    });
  }, [segment, open, allowSeconds]);

  const valueString = useMemo(
    () => displayValue(hours, minutes, seconds, allowSeconds),
    [hours, minutes, seconds, allowSeconds]
  );

  function apply(part: Segment, next: number) {
    if (part === 'h') {
      next = clamp(next, 0, maxHours);
      onChange(fromHMS(next, minutes, seconds));
    } else if (part === 'm') {
      next = clamp(next, 0, 59);
      onChange(fromHMS(hours, next, seconds));
    } else {
      next = clamp(next, 0, 59);
      onChange(fromHMS(hours, minutes, next));
    }
  }

  function step(part: Segment, delta: 1 | -1) {
    if (part === 'h') apply('h', clamp(hours + delta, 0, maxHours));
    if (part === 'm') apply('m', clamp(minutes + delta, 0, 59));
    if (part === 's') apply('s', clamp(seconds + delta, 0, 59));
  }

  function nextSegment(current: Segment, backwards = false): Segment {
    const order: Segment[] = allowSeconds ? ['h', 'm', 's'] : ['h', 'm'];
    const idx = order.indexOf(current);
    const nextIdx = clamp(idx + (backwards ? -1 : 1), 0, order.length - 1);
    return order[nextIdx];
  }

  function handleFocus() {
    setOpen(true);
    setSegment('h');
    setBuffer('');
    setTimeout(() => setCaret('h', 'end'), 0);
  }

  function handleClick(e: React.MouseEvent<HTMLInputElement>) {
    const pos = e.currentTarget.selectionStart ?? 0;
    const ranges = segmentRanges(valueString, allowSeconds);

    if (pos < ranges.m.start) setSegment('h');
    else if (allowSeconds && pos >= ranges.s.start) setSegment('s');
    else setSegment('m');
  }

  function setCaret(seg: Segment, where: 'start' | 'end' = 'end') {
    const el = inputRef.current;
    if (!el) return;
    const r = segmentRanges(valueString, allowSeconds)[seg];
    const pos = where === 'start' ? r.start : r.end;
    requestAnimationFrame(() => {
      try {
        el.setSelectionRange(pos, pos);
      } catch {
        /* empty */
      }
    });
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (disabled) return;
    const key = e.key;
    if (key === 'Tab') {
      e.preventDefault();
      const back = e.shiftKey;
      setSegment((s) => nextSegment(s, back));
      setBuffer('');
      return;
    }
    if (key === 'ArrowUp') {
      e.preventDefault();
      step(segment, 1);
      return;
    }
    if (key === 'ArrowDown') {
      e.preventDefault();
      step(segment, -1);
      return;
    }
    if (key === 'ArrowRight') {
      e.preventDefault();
      const order: Segment[] = allowSeconds ? ['h', 'm', 's'] : ['h', 'm'];
      const idx = order.indexOf(segment);
      const next = order[Math.min(idx + 1, order.length - 1)];
      if (next !== segment) {
        setSegment(next);
        setCaret(next, 'end');
      }
      return;
    }

    if (key === 'ArrowLeft') {
      e.preventDefault();
      const order: Segment[] = allowSeconds ? ['h', 'm', 's'] : ['h', 'm'];
      const idx = order.indexOf(segment);
      const prev = order[Math.max(idx - 1, 0)];
      if (prev !== segment) {
        setSegment(prev);
        setCaret(prev, 'end');
      }
      return;
    }

    if (key === 'Enter') {
      setOpen(false);
      setBuffer('');
      return;
    }
    if (/^[0-9]$/.test(key)) {
      e.preventDefault();
      const nextBuf = (buffer + key).slice(-2);
      setBuffer(nextBuf);
      const num = Number(nextBuf);
      apply(segment, num);
      if (nextBuf.length === 2) {
        // auto-advance
        setSegment((s) => nextSegment(s));
        setBuffer('');
      }
      return;
    }
    if (key.length === 1) {
      e.preventDefault();
    }
  }

  function inc(part: Segment) {
    step(part, 1);
    setSegment(part);
    focusSegment(part);
    setSegment(part);
  }
  function dec(part: Segment) {
    step(part, -1);
    focusSegment(part);
  }
  function focusSegment(part: Segment) {
    setOpen(true);
    setSegment(part);
    setBuffer('');
    inputRef.current?.focus();
  }

  function getScrollParents(el: HTMLElement | null): (HTMLElement | Window)[] {
    const parents: (HTMLElement | Window)[] = [];
    if (!el) return parents;
    let node: HTMLElement | null = el.parentElement;
    const scrollRegex = /(auto|scroll|overlay)/;

    while (node && node !== document.body) {
      const { overflow, overflowY, overflowX } = window.getComputedStyle(node);
      if (
        scrollRegex.test(overflow) ||
        scrollRegex.test(overflowY) ||
        scrollRegex.test(overflowX)
      ) {
        parents.push(node);
      }
      node = node.parentElement;
    }
    parents.push(window);
    return parents;
  }

  useEffect(() => {
    if (!open) return;

    const targets = getScrollParents(wrapperRef.current);
    const onScrollOrResize = () => setOpen(false);

    targets.forEach((t) => {
      if (t instanceof Window) {
        t.addEventListener('scroll', onScrollOrResize, { passive: true });
        t.addEventListener('resize', onScrollOrResize);
      } else {
        t.addEventListener('scroll', onScrollOrResize, { passive: true });
      }
    });

    return () => {
      targets.forEach((t) => {
        if (t instanceof Window) {
          t.removeEventListener('scroll', onScrollOrResize);
          t.removeEventListener('resize', onScrollOrResize);
        } else {
          t.removeEventListener('scroll', onScrollOrResize);
        }
      });
    };
  }, [open]);

  useEffect(() => {
    if (open && wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();

      setPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });
    }
  }, [open]);

  return (
    <div ref={wrapperRef} className={'inline-block w-max' + (className ?? '')}>
      {label && (
        <label className="mb-2 block text-sm font-semibold text-black">
          {label}
        </label>
      )}
      <div className="relative">
        <div className="relative flex w-max items-center gap-2">
          <div
            className={`relative rounded-md border ${
              open && 'border-secondary-04'
            }`}
          >
            <div className="font-mono pointer-events-none absolute inset-0 flex items-center px-3 py-2 text-base">
              <span className={segment === 'h' && open ? 'bg-primary-03' : ''}>
                {two(hours)}
                {t('time.h')}
              </span>
              &nbsp;
              <span className={segment === 'm' && open ? 'bg-primary-03' : ''}>
                {two(minutes)}
                {t('time.m')}
              </span>
              {allowSeconds && (
                <>
                  &nbsp;
                  <span
                    className={segment === 's' && open ? 'bg-primary-03' : ''}
                  >
                    {two(seconds)}
                    {t('time.s')}
                  </span>
                </>
              )}
            </div>

            <input
              ref={inputRef}
              value={valueString}
              onFocus={handleFocus}
              onClick={handleClick}
              onKeyDown={handleKeyDown}
              onChange={() => {}}
              disabled={disabled}
              className={`w-[200px] rounded-md border bg-transparent px-3  py-2 text-base text-transparent caret-black opacity-[0] outline-none ${
                disabled ? 'bg-gray-100' : 'bg-white'
              } ${
                open &&
                'border-secondary-04 caret-transparent selection:bg-transparent'
              }`}
            />
          </div>
          <TimeFilled
            className="absolute right-2 top-[9px] h-6 w-6 cursor-pointer text-grey-07"
            onClick={() => {
              setOpen((o) => {
                const next = !o;
                if (next) {
                  setSegment('h');
                  setBuffer('');
                  // фокус і карет у години (якщо потрібно):
                  inputRef.current?.focus();
                  setTimeout(() => setCaret('h', 'end'), 0);
                }
                return next;
              });
            }}
          />
        </div>

        {open &&
          ReactDOM.createPortal(
            <div
              ref={popupRef}
              role="dialog"
              aria-label="Time editor"
              className="absolute z-20 mt-2 min-w-[385px] rounded-lg bg-white p-4 shadow-xl drop-shadow-m"
              style={{
                top: position.top,
                left: position.left,
                zIndex: 9999,
                position: 'absolute',
              }}
            >
              <div className="grid grid-cols-3 gap-4">
                <TimeColumn
                  title={t('time.hours')}
                  value={two(hours)}
                  onPlus={() => inc('h')}
                  onMinus={() => dec('h')}
                  active={segment === 'h'}
                  onFocus={() => focusSegment('h')}
                />
                <TimeColumn
                  title={t('time.minutes')}
                  value={two(minutes)}
                  onPlus={() => inc('m')}
                  onMinus={() => dec('m')}
                  active={segment === 'm'}
                  onFocus={() => focusSegment('m')}
                />
                {allowSeconds && (
                  <TimeColumn
                    title={t('time.seconds')}
                    value={two(seconds)}
                    onPlus={() => inc('s')}
                    onMinus={() => dec('s')}
                    active={segment === 's'}
                    onFocus={() => focusSegment('s')}
                  />
                )}
              </div>
            </div>,
            document.body
          )}
      </div>
    </div>
  );
}

function TimeColumn({
  title,
  value,
  onPlus,
  onMinus,
  onFocus,
}: {
  title: string;
  value: string;
  onPlus: () => void;
  onMinus: () => void;
  active?: boolean;
  onFocus: () => void;
}) {
  return (
    <div className="flex flex-row items-center gap-3">
      <div className="flex flex-col items-center">
        <div
          className={`min-w-[60px] px-2 py-1 text-[32px] font-semibold leading-[1.1]`}
          onClick={onFocus}
        >
          {value}
        </div>
        <div className="text-sm text-grey-07">{title}</div>
      </div>

      <div className="flex flex-col items-center gap-2">
        <Button
          onClick={onPlus}
          variant="outline"
          onMouseDown={(e) => e.preventDefault()}
          size="sm"
          className="!p-2"
        >
          <Plus className="h-5 w-5" />
        </Button>
        <Button
          onClick={onMinus}
          variant="outline"
          onMouseDown={(e) => e.preventDefault()}
          size="sm"
          className="!p-2"
        >
          <Minus className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
}

function displayValue(h: number, m: number, s: number, allowSeconds: boolean) {
  const base = `${two(h)}h ${two(m)}m`;
  return allowSeconds ? `${base} ${two(s)}s` : base;
}

function segmentRanges(str: string, allowSeconds: boolean) {
  const h = { start: 0, end: 2 };
  const m = { start: 4, end: 6 };
  const s = allowSeconds ? { start: 8, end: 10 } : { start: 7, end: 7 };
  return { h, m, s };
}
